import type { Key } from 'react';
import { getFolder, getFolderDescendants } from '@/services/api/folderService';
import { AppConstants } from '@/utils/constants';
import { DataNode } from 'antd/es/tree';
import { FileOutlined } from '@ant-design/icons';
import { User } from '@/types';
import { Dropdown } from 'antd';
import { getQueryClient } from '@/utils/queryClient';
import { folderKeys } from '@/hooks/useFolders';

export class DocumentView {
  private isAdmin: boolean;
  public treeData: any[] = [];
  public tableData: any[] = [];
  private updateCallback;
  private keyCounter: number = 0;

  constructor(user: User | any, updateCallback: any) {
    this.isAdmin = user?.scopes?.includes(AppConstants.SYSTEM_ADMIN) || false;
    this.updateCallback = updateCallback;
  }

  private generateUniqueKeyForNode(
    nodeId: string,
    parentPath: string = '',
  ): string {
    if (parentPath) {
      return `${parentPath}::${nodeId}`;
    }
    return nodeId;
  }

  public async init() {
    const data = await this.fetchChildren({}, '');
    this.treeData = data;
    this.tableData = data;
    this.updateCallback();
  }

  public async refreshData(params: any = {}) {
    try {
      const data = await this.fetchChildren(params, '');
      if (!params.folder_id) {
        this.treeData = data;
        this.tableData = data;
      } else {
        const key = String(params.folder_id);
        this.treeData = this.updateTreeData(this.treeData, key, data);
        this.tableData = data;
      }
      this.updateCallback();
    } catch (error) {
      // Silent fail
    }
  }

  public async refetchFolderData(params: any = {}) {
    try {
      const queryClient = getQueryClient();
      await queryClient.refetchQueries({
        queryKey: folderKeys.list(params),
      });
      const data = await this.fetchChildren(params, '');
      if (!params.folder_id) {
        this.treeData = data;
        this.tableData = data;
      } else {
        const key = String(params.folder_id);
        this.treeData = this.updateTreeData(this.treeData, key, data);
        this.tableData = data;
      }
      this.updateCallback();
    } catch (error) {
      // Silent fail
    }
  }

  private async fetchChildren(
    key: { folder_id?: string },
    parentPath: string = '',
  ): Promise<any[]> {
    key.folder_id ??= undefined;

    const response = await getFolder(key);
    let { folders = [], files = [] } = response.data || {};

    if (this.isAdmin) {
      const filterByMasterId = (item: any) => item.default === true;
      folders = folders.filter(filterByMasterId);
      files = files.filter(filterByMasterId);
    }

    const seenIds = new Set<string>();

    const mapNode = (item: any, isLeaf: boolean, index: number) => {
      const baseId = item.id || `temp-${this.keyCounter++}`;
      let uniqueId = baseId;

      if (seenIds.has(uniqueId)) {
        uniqueId = `${baseId}-${index}`;
      }
      seenIds.add(uniqueId);

      const nodeKey = this.generateUniqueKeyForNode(uniqueId, parentPath);

      return {
        ...item,
        name: item.name,
        key: nodeKey,
        id: item.id,
        isLeaf,
        rootId: key.folder_id,
        tags: item.tags,
        isRoot: !key.folder_id,
        cmd: key,
        level: 1,
      };
    };

    const allItems = [
      ...folders
        .sort((a: any, b: any) => Number(b.default) - Number(a.default))
        .map((folder: any, index: number) => mapNode(folder, false, index)),
      ...files.map((file: any, index: number) =>
        mapNode(file, true, folders.length + index),
      ),
    ].filter(Boolean);

    return allItems;
  }

  private updateTreeData = (
    list: any[],
    key: Key,
    children: DataNode[],
  ): DataNode[] => {
    if (!key) {
      return children;
    }
    return list.map(node => {
      if (node.key.toString() === key.toString()) {
        return {
          ...node,
          children,
        };
      }
      if (node.children) {
        return {
          ...node,
          children: this.updateTreeData(node.children, key, children),
        };
      }
      return node;
    });
  };

  public transformTreeData = (
    nodes: any,
    treeWidth: number,
    setOpenModal: any,
    items: any,
    seenKeys: Set<string> = new Set(),
  ): DataNode[] => {
    if (!nodes || !Array.isArray(nodes)) return [];

    return nodes.map((node: any, index: number) => {
      let finalKey = node.key || node.id || `node-${index}`;
      let counter = 0;
      const originalKey = finalKey;
      while (seenKeys.has(finalKey)) {
        counter++;
        finalKey = `${originalKey}-${counter}`;
      }
      seenKeys.add(finalKey);

      const className = node.isRoot
        ? node.default
          ? 'tree-leaf-default'
          : 'tree-leaf'
        : '';

      return {
        ...node,
        key: finalKey,
        className,
        switcherIcon: node.isLeaf ? (
          <FileOutlined style={{ width: '10px' }} />
        ) : null,
        title: (
          <Dropdown
            menu={{ items: node.isLeaf ? [] : items(false, node) }}
            trigger={['contextMenu']}
          >
            <div
              className={node.isLeaf ? 'custom-leaf' : ''}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: `${treeWidth - node.level * 24}px`,
                  display: 'inline-block',
                  borderRadius: '2px',
                }}
                title={node.name}
              >
                <span>{node.name}</span>
              </span>
            </div>
          </Dropdown>
        ),
        children: node.children
          ? this.transformTreeData(
              node.children,
              treeWidth,
              setOpenModal,
              items,
              seenKeys,
            )
          : node.children,
      };
    });
  };

  public onLoadData = async (record: any) => {
    let parent = record;
    const key = String(record?.key || '');
    const originalId = record?.id || (key ? key.split('::').pop() : '') || key;

    let body = {
      project_id: null,
      folder_id: originalId,
    };

    if (record?.isBack) {
      body = record?.parent?.cmd;
    }

    if (record?.isBack && record?.parent) {
      parent = record?.parent?.parent;
    }

    // Always use the full key as parentPath so children get unique
    // path-based keys (e.g. "1::2" instead of just "2").  This prevents
    // key collisions when different root folders have children with the
    // same numeric ID.
    const parentPath = key;

    const children = await this.fetchChildren(body, parentPath);
    const newChildren = children.map((elements: any) => ({
      ...elements,
      level: (record?.level || 1) + 1,
      parent: parent,
    }));
    this.treeData = this.updateTreeData(this.treeData, key, newChildren);
    this.tableData = newChildren;
    this.updateCallback();
  };

  public async fetchChildrenForNode(node: any): Promise<any> {
    const key = String(node?.key || '');
    const originalId = node?.id || (key ? key.split('::').pop() : '') || key;
    const parentPath = key;

    const body = {
      project_id: null,
      folder_id: originalId,
    };

    const children = await this.fetchChildren(body, parentPath);
    const newChildren = children.map((element: any) => ({
      ...element,
      level: (node?.level || 1) + 1,
      parent: { ...node, children: null },
    }));

    this.treeData = this.updateTreeData(this.treeData, key, newChildren);
    this.updateCallback();

    return children;
  }

  /**
   * Get all descendant folder IDs and file IDs for a folder in a
   * single API call.  The backend handles the recursive traversal.
   */
  public async getDescendantIds(
    folderId: string,
  ): Promise<{ folderIds: string[]; fileIds: string[] }> {
    const response = await getFolderDescendants(folderId);
    const data = response.data || { folder_ids: [], file_ids: [] };
    return {
      folderIds: data.folder_ids || [],
      fileIds: data.file_ids || [],
    };
  }

  public findNodeInTree = (nodeId: string, treeData: any[]): any => {
    for (const element of treeData) {
      if (element.id === nodeId) return element;
      if (element?.children) {
        const found = this.findNodeInTree(nodeId, element.children);
        if (found) return found;
      }
    }
    return null;
  };

  public backToNode = (node: any) => {
    if (!node) {
      this.tableData = this.treeData;
    } else {
      const found = this.findNodeInTree(node.id, this.treeData);
      this.tableData = found?.children || [];
    }
    this.updateCallback();
  };

  private removeNodeFromTree = (
    treeData: any[],
    targetNodeId: string,
  ): any[] => {
    return treeData
      .filter(node => node.id !== targetNodeId)
      .map(node => ({
        ...node,
        children: node.children
          ? this.removeNodeFromTree(node.children, targetNodeId)
          : [],
      }));
  };

  public addNode = (currentNode: any, newNode: any) => {
    const isFolder = !newNode.type;

    const addNodeToTree = (treeData: any[]) => {
      if (!currentNode?.id) {
        const nodeKey = this.generateUniqueKeyForNode(newNode.id, '');
        const formattedNode = {
          ...newNode,
          key: nodeKey,
          isLeaf: !isFolder,
          level: 1,
          default: this.isAdmin,
          isRoot: true,
          rootId: undefined,
          cmd: { folder_id: undefined, project_id: null },
        };
        // Find the position to insert - after other folders but before files
        const lastFolderIndex = treeData.findIndex(
          (node: any) => node.isLeaf === true,
        );
        const insertIndex =
          lastFolderIndex === -1 ? treeData.length : lastFolderIndex;

        this.treeData = [
          ...treeData.slice(0, insertIndex),
          formattedNode,
          ...treeData.slice(insertIndex),
        ];
        this.tableData = [
          ...this.tableData.slice(0, insertIndex),
          formattedNode,
          ...this.tableData.slice(insertIndex),
        ];
        return;
      }
      for (const element of treeData) {
        const elementId = element.id || element.key?.split('::').pop();
        if (elementId === currentNode?.id || element.key === currentNode?.key) {
          const parentPath = element.key || '';
          const nodeKey = this.generateUniqueKeyForNode(newNode.id, parentPath);
          const formattedNode = {
            ...newNode,
            key: nodeKey,
            isLeaf: !isFolder,
            level: element.level + 1,
            parent: { ...element, children: null },
            rootId: element.id,
            cmd: { folder_id: element.id, project_id: null },
          };
          if (!element.children) {
            element.children = [];
          }
          // Find the position to insert - after other folders but before files
          const lastFolderIndex = element.children.findIndex(
            (node: any) => node.isLeaf === true,
          );
          const insertIndex =
            lastFolderIndex === -1 ? element.children.length : lastFolderIndex;

          element.children = [
            ...element.children.slice(0, insertIndex),
            formattedNode,
            ...element.children.slice(insertIndex),
          ];

          // Also insert in tableData at the right position
          const tableLastFolderIndex = this.tableData.findIndex(
            (node: any) => node.isLeaf === true,
          );
          const tableInsertIndex =
            tableLastFolderIndex === -1
              ? this.tableData.length
              : tableLastFolderIndex;

          this.tableData = [
            ...this.tableData.slice(0, tableInsertIndex),
            formattedNode,
            ...this.tableData.slice(tableInsertIndex),
          ];
          return;
        }
        if (element?.children) {
          addNodeToTree(element.children);
        }
      }
    };
    addNodeToTree(this.treeData);
    this.updateCallback();
  };

  public addLeaves = (currentNode: any, newLeaves: any[]) => {
    let formattedLeaves: any[] = [];
    const addNodeToTree = (treeData: any[]) => {
      if (!currentNode?.id) {
        formattedLeaves = newLeaves.map((leaf, index) => {
          const nodeKey = this.generateUniqueKeyForNode(leaf.id, '');
          return {
            ...leaf,
            key: nodeKey,
            isLeaf: true,
            level: 1,
            isRoot: true,
            default: this.isAdmin,
          };
        });
        this.treeData = [...this.treeData, ...formattedLeaves];
        this.tableData = [...this.tableData, ...formattedLeaves];
        return;
      }
      for (const element of treeData) {
        const elementId = element.id || element.key?.split('::').pop();
        if (elementId === currentNode?.id || element.key === currentNode?.key) {
          const parentPath = element.key || '';
          formattedLeaves = newLeaves.map((leaf, index) => {
            const nodeKey = this.generateUniqueKeyForNode(leaf.id, parentPath);
            return {
              ...leaf,
              key: nodeKey,
              isLeaf: true,
              level: element.level + 1,
              parent: { ...element, children: null },
            };
          });
          if (!element.children) {
            element.children = [];
          }
          element.children = [...(element.children || []), ...formattedLeaves];
          this.tableData = [...this.tableData, ...formattedLeaves];
          return;
        }
        if (element?.children && currentNode) {
          addNodeToTree(element.children);
        }
      }
    };
    addNodeToTree(this.treeData);
    this.updateCallback();
  };

  public removeNode = (targetNode: any) => {
    this.treeData = this.removeNodeFromTree(this.treeData, targetNode.id);
    this.tableData = this.tableData.filter(
      element => element.id != targetNode.id,
    );
    this.updateCallback();
  };

  public updateNodeName = (targetNode: any) => {
    const updateNodeFromTree = (treeData: any[]) => {
      for (const element of treeData) {
        if (element.id === targetNode.id) {
          element.name = targetNode.name;
          if (element.isLeaf) {
            element.path = targetNode.path;
          }
          return;
        }
        if (element?.children) {
          updateNodeFromTree(element.children);
        }
      }
    };
    updateNodeFromTree(this.treeData);
    this.tableData = [
      ...this.tableData.map(node => ({
        ...node,
        name: node.id === targetNode.id ? targetNode.name : node.name,
      })),
    ];
    this.updateCallback();
  };

  public moveBranch = (movingNode: any, targetId: string) => {
    this.treeData = this.removeNodeFromTree(this.treeData, movingNode.id);
    const targetIdStr = targetId ? String(targetId) : '';
    let targetFound = false;

    const addNodeToTree = (treeData: any[]): boolean => {
      for (const element of treeData) {
        if (String(element.id) === targetIdStr) {
          const parentChildren = Array.isArray(element.children)
            ? element.children
            : [];
          element.children = [
            ...parentChildren,
            {
              ...movingNode,
              isRoot: false,
              parent: { ...element, children: [] },
              rootId: element.id,
              cmd: { folder_id: element.id, project_id: null },
            },
          ];
          return true;
        }
        if (element?.children) {
          const moved = addNodeToTree(element.children);
          if (moved) {
            return true;
          }
        }
      }
      return false;
    };

    if (targetIdStr) {
      targetFound = addNodeToTree(this.treeData);
    }

    if (!targetIdStr || !targetFound) {
      const rootNode = {
        ...movingNode,
        isRoot: true,
        parent: undefined,
        rootId: undefined,
        cmd: { folder_id: undefined, project_id: null },
        level: 1,
      };
      const firstLeafIndex = this.treeData.findIndex(
        (node: any) => node.isLeaf === true,
      );
      const insertIndex =
        firstLeafIndex === -1 ? this.treeData.length : firstLeafIndex;
      this.treeData = [
        ...this.treeData.slice(0, insertIndex),
        rootNode,
        ...this.treeData.slice(insertIndex),
      ];
    }

    this.tableData = this.tableData.filter(node => node.id != movingNode.id);
    this.updateCallback();
  };
}
